/**
 * メール送付の1回目（#225、docs/91。2026-10-21 射場さんの決め ★1〜★5）。見積書・請求書・領収書（と見積依頼書）をメールで送る。
 *   ・送信方法「印刷・FAX｜メール」を帳票の紙の画面の右の枠で切り替える。送る PDF を左に見ながら右で書く
 *   ・渡し方は「閲覧ページ」（既定＝★1）か「添付」。閲覧ページは公開期限（既定 45日・最大 60日）・無効にできる・取得を記録して送った人に知らせる（★4）
 *   ・パスワードは使わない（★2）。本文に URL を書けない（閲覧ページは［閲覧ページ］で差し込む）
 *   ・差し込みは日本語の札（送付状とそろえる）。メールの文面は書類の種類で自動（会社に1つ。見積依頼書は設定「見積依頼」のまま）
 * 画面の文はここ（MAIL_MSG）に1か所
 */
import { z } from "zod";
import { CoverLetterContent, DeliveryTarget } from "./coverLetter.js";

/* ============================================================================
 * 決まり
 * ========================================================================== */

/** 書類の種類（メールの文面を選ぶ）。送る書類の1つ目で決まる */
export const MailKind = z.enum(["quote", "invoice", "receipt", "rfq", "order", "revise", "decline", "payment_notice", "delivery_note", "acceptance"]);
export type MailKind = z.infer<typeof MailKind>;
/** 渡し方：page＝閲覧ページ／attach＝添付 */
export const MailMode = z.enum(["page", "attach"]);
export type MailMode = z.infer<typeof MailMode>;

/** 上限（DB の CHECK・API と同じ値）。1日の通数・添付の合計は見積依頼と同じ（RFQ_LIMITS） */
export const MAIL_LIMITS = {
  /** 1社 1日にメールで送れる通数の既定（帳票と見積依頼書を合わせて。送信失敗は数えない）。会社ごとの値は tenants.mail_per_day（運営が管理アプリで変える。docs/91 §16） */
  perDay: 50,
  /**
   * 全社の送る速さの守り（docs/91 §16）：送り口（さくらのアカウント）は製品ごとに全社で共有＝15分 約100宛先が上限。
   * 同じ製品の全部の会社の直近 rateWindowMin 分のメールを 1通＝rateUnitsPerMail 宛先（控え Bcc）で数え、送ったあと ratePer15m を超えるなら 429 MAIL_RATE_LIMIT
   */
  ratePer15m: 80,
  rateUnitsPerMail: 2,
  rateWindowMin: 15,
  /** 添付の合計 */
  attachBytes: 10 * 1024 * 1024,
  /** 閲覧ページで渡すファイルの合計 */
  pageBytes: 20 * 1024 * 1024,
  /** 追加するファイル1つ */
  fileBytes: 10 * 1024 * 1024,
  /** 追加するファイルの数 */
  uploads: 10,
  /** 宛先・Cc・Bcc それぞれ */
  recipients: 10,
  /** 公開期限（日） */
  expiresMin: 1,
  expiresMax: 60,
  expiresDefault: 45,
  /** 未取得が何日続いたら「本日の業務」に出すか（★4） */
  unopenedDays: 7,
  /** メールの文面（設定）の件名・本文 */
  templateSubject: 100,
  templateBody: 2000,
  /** 送る1通の件名・本文 */
  subject: 200,
  body: 4000,
  /** 無効にする理由 */
  revokeReason: 200,
} as const;

/** 追加できるファイル（拡張子 → 形式）。中身の頭で形式を確かめる（API の mail/uploads.ts） */
export const MAIL_UPLOAD_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  doc: "application/msword",
  xls: "application/vnd.ms-excel",
  ppt: "application/vnd.ms-powerpoint",
  zip: "application/zip",
};
export const MAIL_UPLOAD_ACCEPT = Object.keys(MAIL_UPLOAD_TYPES)
  .map((e) => `.${e}`)
  .join(",");
/** ファイル名の拡張子（小文字）。無ければ "" */
export const extOf = (name: string): string => /\.([A-Za-z0-9]+)$/.exec(name)?.[1]?.toLowerCase() ?? "";

/* ============================================================================
 * 差し込み（日本語の札。送付状の ［宛名］［件名］［書類］［自社名］［担当者］ とそろえる）
 * ========================================================================== */

export const MAIL_FIELDS = ["［宛名］", "［件名］", "［書類］", "［番号］", "［金額］", "［お支払期限］", "［閲覧ページ］", "［期限］", "［自社名］", "［担当者］", "［送信者］"] as const;
/** {order}の文面だけ：承諾ページ（#233、docs/93 §7。電子承諾のとき。無ければその行を消す） */
export const MAIL_FIELDS_ORDER = [...MAIL_FIELDS, "［承諾ページ］"] as const;
/** {order}の訂正版の文面（#233 2b）：前回の番号（PO-0001。訂正版は［番号］が PO-0001-2） */
export const MAIL_FIELDS_REVISE = [...MAIL_FIELDS_ORDER, "［前回の番号］"] as const;
/** {paymentNotice}の文面（#233 3b、docs/93 §15.6）：確認ページ（［確認ページ］）。［期限］は確認の期限（送付後 n日） */
export const MAIL_FIELDS_NOTICE = [...MAIL_FIELDS, "［確認ページ］"] as const;
/** 辞退の連絡（#233 2a、docs/93 §14.4）の札：金額・期限・閲覧ページは入れない */
export const MAIL_FIELDS_DECLINE = ["［宛名］", "［件名］", "［書類］", "［自社名］", "［担当者］"] as const;
/** 見積依頼書の文面で使える札（設定「見積依頼」と同じ＝RFQ_MAIL_FIELDS） */
export type MailFieldValues = {
  to: string;
  subject: string;
  docs: string;
  no: string;
  amount: string;
  dueDate: string;
  url: string;
  expires: string;
  company: string;
  staff: string;
  sender: string;
  /** {order}の承諾ページの URL（#233。電子承諾でないとき・ほかの書類は空＝その行を消す） */
  ackUrl?: string;
  /** {order}の訂正版の前回の番号（#233 2b。ほかの書類は空） */
  prevNo?: string | undefined;
  /** {paymentNotice}の確認ページの URL（#233 3b。ほかの書類は空＝その行を消す） */
  confirmUrl?: string | undefined;
};

/**
 * 差し込みを埋める。添付のとき（mode=attach）は［閲覧ページ］［期限］のある行を消す（「下記よりご確認ください」の URL の行など）。
 * 消したあとに空の行が3つ以上続けば2つに
 */
export function fillMail(text: string, v: MailFieldValues, mode: MailMode): string {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  // 確認ページのある文面（{paymentNotice}。#233 3b）は［期限］＝確認の期限なので、添付でもその行を消さない
  const base = mode === "attach" ? lines.filter((l) => !l.includes("［閲覧ページ］") && (!!v.confirmUrl || !l.includes("［期限］"))) : lines;
  // 承諾ページが無い（電子承諾でない{order}）：［承諾ページ］の行と、そのすぐ前の承諾のお願いの行を消す（#233）
  const kept0 = v.ackUrl ? base : base.filter((l, i, xs) => !l.includes("［承諾ページ］") && !(l.includes("承諾") && xs[i + 1]?.includes("［承諾ページ］")));
  // 確認ページが無い（{paymentNotice}でない・鍵が無い）：［確認ページ］の行と、そのすぐ前の確認のお願いの行を消す（#233 3b）
  const kept = v.confirmUrl ? kept0 : kept0.filter((l, i, xs) => !l.includes("［確認ページ］") && !(l.includes("確認") && xs[i + 1]?.includes("［確認ページ］")));
  return kept
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .replaceAll("［宛名］", v.to)
    .replaceAll("［件名］", v.subject)
    .replaceAll("［書類］", v.docs)
    .replaceAll("［番号］", v.no)
    .replaceAll("［金額］", v.amount)
    .replaceAll("［お支払期限］", v.dueDate)
    .replaceAll("［閲覧ページ］", v.url)
    .replaceAll("［期限］", v.expires)
    .replaceAll("［自社名］", v.company)
    .replaceAll("［担当者］", v.staff)
    .replaceAll("［送信者］", v.sender)
    .replaceAll("［承諾ページ］", v.ackUrl ?? "")
    .replaceAll("［前回の番号］", v.prevNo ?? "")
    .replaceAll("［確認ページ］", v.confirmUrl ?? "");
}

/** 件名・本文の URL（閲覧ページの差し込みの外）。迷惑メールに入りにくくし、任意の URL を送らない（docs/91 §5） */
const URL_RE = /(https?:\/\/|www\.[a-z0-9-]+\.)/i;
export const hasUrl = (text: string): boolean => URL_RE.test(text);

/** 「2026年11月30日」（YYYY-MM-DD から） */
export const jpDateText = (ymd: string | null | undefined): string => {
  const m = ymd ? /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd) : null;
  return m ? `${Number(m[1])}年${Number(m[2])}月${Number(m[3])}日` : "";
};
/** 今日（YYYY-MM-DD）から n 日後（日本時間の日付として） */
export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const t = new Date(Date.UTC(y!, m! - 1, d! + days));
  return t.toISOString().slice(0, 10);
}
/** 大きさ「1.2MB」「340KB」 */
export function sizeText(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(Math.ceil((bytes / 1024 / 1024) * 10) / 10).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}
/** 渡し方の上限 */
export const mailBytesLimit = (mode: MailMode): number => (mode === "page" ? MAIL_LIMITS.pageBytes : MAIL_LIMITS.attachBytes);

/** メールの文面の既定（会社が直していないとき。両製品とも同じ）。見積依頼書は PRODUCTS[*].rfq のまま */
export const MAIL_TEMPLATE_DEFAULTS: Record<"quote" | "invoice" | "receipt" | "order" | "revise" | "decline" | "payment_notice" | "delivery_note" | "acceptance", { subject: string; body: string }> = {
  quote: {
    subject: "御見積書のご送付（［件名］）",
    body: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\n［件名］の御見積書をお送りいたします。\nご確認のほど、よろしくお願いいたします。",
  },
  invoice: {
    subject: "ご請求書のご送付（［件名］）",
    body: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\n［件名］のご請求書をお送りいたします。\n\nご請求金額：［金額］\nお支払期限：［お支払期限］\n\nご確認のほど、よろしくお願いいたします。",
  },
  receipt: {
    subject: "領収書のご送付（［件名］）",
    body: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\n［件名］の領収書をお送りいたします。\nこのたびはお支払いいただき、誠にありがとうございました。",
  },
  // #233（docs/93 §7）：［書類］は会社の言葉の帳票の名前（注文書／発注書…）。電子承諾のときは［承諾ページ］の行が入る
  order: {
    subject: "［書類］のご送付（［件名］）",
    body: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\n［件名］の［書類］をお送りいたします。\n内容をご確認のうえ、下記のページから承諾をお願いいたします。\n［承諾ページ］\n\nよろしくお願いいたします。",
  },
  // #233 2b（docs/93 §14.5）：{order}の訂正版（2回目から）。［前回の番号］＝差し替える前の回の番号・［番号］＝訂正版の番号（枝番）
  revise: {
    subject: "訂正版の承諾のお願い（［件名］）",
    body: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\n［件名］の［書類］（［前回の番号］）の内容を変更しましたので、訂正版（［番号］）をお送りいたします。\n内容をご確認のうえ、下記のページから承諾をお願いいたします。\n［承諾ページ］\n\nよろしくお願いいたします。",
  },
  // #233 3b（docs/93 §15.6）：［書類］は会社の言葉の帳票の名前（支払通知書／支払明細書）。［期限］は確認の期限（送付後 n日）・［確認ページ］は確認ページ
  payment_notice: {
    subject: "［書類］のご送付（［件名］）",
    body: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\n［件名］の［書類］をお送りいたします。\n［期限］までに、下記のページから内容のご確認をお願いいたします。\n［確認ページ］\n\nご連絡がない場合は、確認いただいたものとさせていただきます。\nよろしくお願いいたします。",
  },
  // #266（docs/96）：［書類］は会社の言葉の帳票の名前（納品書／出荷案内書…）。金額は入れない（税を確定する書類が請求書の顧客は税抜のため）
  delivery_note: {
    subject: "［書類］のご送付（［件名］）",
    body: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\n［件名］の［書類］をお送りいたします。\nご確認のほど、よろしくお願いいたします。",
  },
  // #266 1b：［書類］は会社の言葉の帳票の名前（検収書／物品受領書／作業報告書／完了報告書…）。検収のボタンは閲覧ページ（メールの本文に URL は書けない）
  acceptance: {
    subject: "［書類］のご送付（［件名］）",
    body: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\n［件名］の［書類］をお送りいたします。\n内容をご確認のうえ、検収のご記録をお願いいたします。",
  },
  // #233 2a（docs/93 §14.4）：不採用の{vendor}への辞退の連絡（任意・自動では送らない）。［書類］は「御見積書」・金額は入れない
  decline: {
    subject: "御見積書の件（［件名］）",
    body: "［宛名］\n\nいつもお世話になっております。［自社名］の［担当者］です。\nこのたびは［件名］の［書類］をお送りいただき、誠にありがとうございました。\n社内で検討いたしました結果、誠に恐縮ながら、今回は見送らせていただくことになりました。\nご期待に沿えず申し訳ございません。今後ともよろしくお願いいたします。",
  },
};

/* ============================================================================
 * 文（画面・メール）
 * ========================================================================== */

/** 書類の種類の名前（{order}は会社の言葉＝画面は t() を通す） */
export const MAIL_KIND_LABEL: Record<MailKind, string> = { quote: "見積書", invoice: "請求書", receipt: "領収書", rfq: "見積依頼書", order: "{order}", revise: "{order}（訂正版）", decline: "辞退の連絡", payment_notice: "{paymentNotice}", delivery_note: "{deliveryNote}", acceptance: "{acceptance}" };
export const MAIL_MODE_LABEL: Record<MailMode, string> = { page: "閲覧ページ", attach: "添付" };
export const MAIL_STATE_LABEL = {
  queued: "送信中",
  sent: "送信済",
  unopened: "未取得",
  opened: "取得済",
  bounced: "届いていません",
  revoked: "無効",
  expired: "期限切れ",
  failed: "送信失敗",
} as const;
export const MAIL_ACCESS_LABEL = { open: "閲覧ページ", view: "表示", download: "ダウンロード", zip: "一括ダウンロード" } as const;

export const MAIL_MSG = {
  // 帳票の紙の画面の右の枠
  via: "送信方法",
  viaPrint: "印　刷", // 2026-10-22：「印刷・FAX」から（間は全角の空白）
  viaMail: "メール",
  sectionTo: "送信先",
  to: "宛先",
  cc: "Cc",
  bcc: "Bcc",
  ccBcc: "Cc・Bcc",
  bccSelf: "控え Bcc",
  bccSelfHelp: "送信者のメールアドレス（{email}）にも Bcc で送ります。",
  addAddress: "宛先追加",
  removeAddress: "削除",
  registerEmail: "{customer}に登録",
  registerEmailHelp: "チェックを入れると、送信のときにこのメールアドレスを{customer}担当者（{contact}）のメールアドレスとして登録します。",
  noEmail: "{customer}担当者のメールアドレスが未登録です",
  toRequired: "宛先を入力してください",
  emailInvalid: "メールアドレスの形を確かめてください",
  tooManyAddresses: "宛先・Cc・Bcc はそれぞれ 10 件までです",
  subject: "件名",
  body: "本文",
  subjectRequired: "件名を入力してください",
  bodyRequired: "本文を入力してください",
  fields: "差し込み",
  fieldsHelp:
    "札を押すと本文に入ります。［閲覧ページ］は閲覧ページの URL、［期限］は公開期限になります（添付のときはその行を消します）。閲覧ページで送るときは、本文の後ろに「書類表示」のボタンが入ります。",
  urlNotAllowed: "件名・本文に URL は入力できません（閲覧ページは［閲覧ページ］で差し込みます）",
  mode: "渡し方",
  modeHelp:
    "閲覧ページ：書類はメールに入れず、ログインのいらないページで表示・ダウンロードしてもらいます。取得したかが分かり、公開期限が過ぎるか無効にすると開けなくなります。添付：書類をメールに添付します（取得したかは分かりません）。",
  expires: "公開期限",
  expiresDays: "日",
  expiresUntil: "{d} まで",
  expiresRange: "公開期限は 1〜60 日です",
  cover: "送付状",
  coverHelp: "送付状の PDF を1つ目のファイルにして送ります。",
  files: "送付書類",
  addSameRecipient: "書類追加",
  addFile: "ファイル追加",
  fileHelp: "PDF・画像（PNG・JPEG）・Office（Word・Excel・PowerPoint）・ZIP（1ファイル 10MB まで・10件まで）",
  uploading: "ファイル読み込み中…",
  total: "{n}件・合計 {size}",
  overAttach: "添付は合計 {limit} までです（現在 {size}）",
  overPage: "閲覧ページは合計 {limit} までです（現在 {size}）",
  send: "送信",
  sending: "送信中…",
  making: "メール作成中…",
  sent: "メールを送信しました",
  failed: "メールを送信できませんでした（送付の記録に「送信失敗」で残しました）",
  perDayLimit: "メールは1日 {n}通までです。明日送るか、印刷・FAX で送ってください",
  rateLimit: "混み合っています。数分後にもう一度お試しください",
  companyRequired: "自社名を設定してください（設定 → 自社情報）",
  docNotIssued: "メールで送れるのは発行した帳票だけです",
  // 確認の窓
  confirmTitle: "送信",
  confirmTo: "宛先",
  confirmMode: "渡し方",
  confirmFiles: "送付書類",
  confirmFilesText: "{n}件（合計 {size}）",
  confirmCover: "送付状を含む",
  // 見本（左）
  preview: "メールの見本",
  previewFrom: "差出人",
  previewSubject: "件名",
  pendingUrl: "https://（送信のときに作成）",
  // メール
  mailFooter: "このメールは {company} が{product}から送りました。",
  mailFooterAttach: "書類を {n}件 添付しています。",
  mailButton: "書類表示",
  mailExpires: "公開期限：{d}",
  /** 電子承諾の{order}（#233 §13.7 イ）：ボタンは承諾ページ・閲覧ページは作らない */
  mailAckExpires: "承諾期限：{d}",
  modeAck: "承諾ページ",
  modeAckHelp: "承諾ページ：本文の後ろの「書類表示」のボタンが承諾ページ（{order}の内容と「承諾」のボタン）になります。閲覧ページは作りません。添付：書類をメールに添付します（承諾ページの URL は本文に入ります）。",
  ackExpires: "承諾期限",
  /** {paymentNotice}（#233 3b）：渡し方の「確認ページ」・ボタンの下の1行・送信の確認 */
  mailConfirmExpires: "確認の期限：{d}",
  modeConfirm: "確認ページ",
  modeConfirmHelp: "確認ページ：本文の後ろの「書類表示」のボタンが確認ページ（{paymentNotice}の内容と「確認」のボタン）になります。閲覧ページは作りません。添付：書類をメールに添付します（確認ページの URL は本文に入ります）。",
  confirmExpires: "確認の期限",
  // 閲覧ページ（受け取る人の画面）
  pageTitle: "書類のご案内",
  pageSentAt: "送信日",
  pageExpires: "公開期限",
  pageView: "表示",
  pageDownload: "ダウンロード",
  pageZip: "一括ダウンロード",
  pagePowered: "このページは {company} が{product}で公開しています",
  pageGone: "この書類は公開を終了しました",
  pageGoneBody: "公開期限が過ぎたか、送信者が公開を終了しました。必要なときは {company} にお問い合わせください。",
  pageNotFound: "ページが見つかりません",
  pageNotFoundBody: "URL を確かめてください。メールの URL が途中で切れていることがあります。",
  pageLoading: "読み込み中…",
  pageClose: "閉じる",
  pagePages: "{n}ページ",
  tooMany: "操作が続いたため一時的に止めています。しばらくしてからもう一度開いてください",
  // 送付の記録
  stateTitle: "状態",
  sectionMail: "メール",
  revoke: "閲覧ページ無効化",
  revokeTitle: "閲覧ページ無効化",
  revokeReason: "理由",
  revokeAsk: "この送付の閲覧ページを開けないようにします。元に戻せません。",
  revoked: "閲覧ページを無効にしました",
  dismiss: "確認済み",
  dismissed: "確認済みにしました",
  dismissHelp: "「本日の業務」の「メールの確認」から外します。",
  accesses: "取得の記録",
  accessesEmpty: "取得はまだありません",
  accessSelf: "自社",
  accessSelfHelp: "送った会社でログイン中の利用者が開いた記録です（取得に数えません）。",
  sentFiles: "送付書類",
  bouncedAt: "不達の知らせ",
  error: "送信できなかった理由",
  tabAll: "すべて",
  tabAttention: "要確認",
  // 設定 → 見積・請求 → メールの文面
  settingsSection: "メールの文面",
  settingsHelp: "帳票をメールで送るときの件名・本文の既定です。送るたびに、その1通だけ書き換えられます。見積依頼書は設定「見積依頼」と同じ文面です。",
  settingsReset: "既定に戻す",
  settingsResetDone: "既定に戻しました",
  // {customer}の詳細（送付状の節）
  partnerMailMode: "メールの渡し方",
  partnerMailModeDefault: "既定（{mode}）",
  partnerSaved: "メールの渡し方を保存しました",
  // お知らせ・ホーム
  noticeOpened: "{to} が {docs} を取得しました",
  homeCard: "メールの確認",
  homeBounced: "届いていません {n}件",
  homeUnopened: "未取得 {d}日 {n}件",
  // 追加するファイル
  uploadType: "PDF・画像（PNG・JPEG）・Office・ZIP のファイルを選択してください",
  uploadTooBig: "ファイルは 10MB までです",
  uploadTooMany: "追加できるファイルは 10件までです",
  uploadNotFound: "ファイルが見つかりません",
  deliveryNotFound: "送付の記録が見つかりません",
  notMail: "メールの送付ではありません",
} as const;

/* ============================================================================
 * 型（API）
 * ========================================================================== */

const Email = z.string().trim().toLowerCase().email(MAIL_MSG.emailInvalid).max(254);
const Emails = z.array(Email).max(MAIL_LIMITS.recipients, MAIL_MSG.tooManyAddresses);

/** 送る1通の中身（枠で書いたもの。差し込みは API が埋める） */
export const MailContent = z
  .object({
    to: Emails.min(1, MAIL_MSG.toRequired),
    cc: Emails.default([]),
    bcc: Emails.default([]),
    bccSelf: z.boolean().default(true),
    subject: z.string().trim().min(1, MAIL_MSG.subjectRequired).max(MAIL_LIMITS.subject),
    body: z.string().trim().min(1, MAIL_MSG.bodyRequired).max(MAIL_LIMITS.body),
    mode: MailMode,
    expiresDays: z.number().int().min(MAIL_LIMITS.expiresMin, MAIL_MSG.expiresRange).max(MAIL_LIMITS.expiresMax, MAIL_MSG.expiresRange).default(MAIL_LIMITS.expiresDefault),
  })
  .superRefine((v, ctx) => {
    if (hasUrl(v.subject)) ctx.addIssue({ code: "custom", path: ["subject"], message: MAIL_MSG.urlNotAllowed });
    if (hasUrl(v.body)) ctx.addIssue({ code: "custom", path: ["body"], message: MAIL_MSG.urlNotAllowed });
  });
export type MailContent = z.infer<typeof MailContent>;
export type MailContentInput = z.input<typeof MailContent>;

/** POST /mail-deliveries/draft：メールの初めの値 */
export const MailDraftRequest = z.object({ target: DeliveryTarget });
export const MailDraftFile = z.object({
  key: z.string(),
  kind: z.enum(["quote", "invoice", "receipt", "rfq", "order", "payment_notice", "delivery_note", "acceptance", "received", "file"]),
  label: z.string(),
  docNo: z.string().nullable(),
  fileName: z.string(),
  pages: z.number().int(),
  bytes: z.number().int(),
});
export type MailDraftFile = z.infer<typeof MailDraftFile>;
export const MailDraft = z.object({
  kind: MailKind,
  /** 書類の種類のメールの文面（差し込みは埋める前） */
  subject: z.string(),
  body: z.string(),
  /** 差し込みの値（url・expires は画面が渡し方と公開期限から入れる） */
  values: z.object({ to: z.string(), subject: z.string(), docs: z.string(), no: z.string(), amount: z.string(), dueDate: z.string(), company: z.string(), staff: z.string(), sender: z.string(), replyBy: z.string(), prevNo: z.string().optional() }),
  /** 宛先の初めの値（帳票の担当者のメール。無ければ空） */
  to: z.array(z.string()),
  /** 宛先の候補（その{customer}の担当者のうちメールのある人） */
  candidates: z.array(z.object({ email: z.string(), label: z.string() })),
  /** 帳票の担当者（メールが無ければ「{customer}に登録」の先） */
  contact: z.object({ id: z.string().uuid(), label: z.string(), email: z.string().nullable() }).nullable(),
  mode: MailMode,
  modeSource: z.enum(["partner", "default"]),
  expiresDays: z.number().int(),
  /** 控え Bcc の宛先（送った人） */
  senderEmail: z.string(),
  /** 送る書類（送付状は入らない） */
  files: z.array(MailDraftFile),
  bytes: z.number().int(),
  /** 今日すでに送った通数（会社の1日の通数＝perDay まで） */
  sentToday: z.number().int(),
  /** この会社の1日の通数（tenants.mail_per_day。既定 MAIL_LIMITS.perDay。docs/91 §16） */
  perDay: z.number().int(),
  /** 電子承諾の{order}の承諾ページの期限（日本時間の日 YYYY-MM-DD）。あれば渡し方は「承諾ページ｜添付」＝page はボタンを承諾ページにして閲覧ページを作らない（#233 §13.7 イ）。ほかの書類は null */
  ackExpiresOn: z.string().nullable().default(null),
  /**
   * {paymentNotice}の確認の期限（日本時間の日 YYYY-MM-DD。いま送ったときの 送付の日＋n日・送ってあればその日）。あれば渡し方は「確認ページ｜添付」＝page はボタンを
   * 確認ページにして閲覧ページを作らない・［期限］は確認の期限（#233 3b）。ほかの書類は null
   */
  confirmOn: z.string().nullable().default(null),
});
export type MailDraft = z.infer<typeof MailDraft>;

/** POST /mail/uploads の答え（追加したファイル） */
export const MailUpload = z.object({ id: z.string().uuid(), name: z.string(), bytes: z.number().int(), contentType: z.string(), pages: z.number().int().nullable() });
export type MailUpload = z.infer<typeof MailUpload>;

/** POST /mail-deliveries：送る */
export const MailDeliveryCreate = z.object({
  target: DeliveryTarget,
  mail: MailContent,
  /** 送付状（null＝無し。既定オフ） */
  cover: CoverLetterContent.nullable().default(null),
  /** 追加したファイル（POST /mail/uploads の id と名前＝添付の名前。並びのまま書類の後ろ） */
  uploads: z
    .array(z.object({ id: z.string().uuid(), name: z.string().trim().min(1).max(200) }))
    .max(MAIL_LIMITS.uploads, MAIL_MSG.uploadTooMany)
    .default([]),
  /** その場で打った宛先を{customer}担当者のメールに登録（既定オフ） */
  saveEmail: z.object({ contactId: z.string().uuid(), email: Email }).nullable().default(null),
});
export type MailDeliveryCreate = z.infer<typeof MailDeliveryCreate>;
export type MailDeliveryCreateInput = z.input<typeof MailDeliveryCreate>;
export const MailDeliveryResult = z.object({ id: z.string().uuid(), mode: MailMode, files: z.number().int(), bytes: z.number().int() });
export type MailDeliveryResult = z.infer<typeof MailDeliveryResult>;

/** メールの文面（GET /company/mail-templates）。source＝会社が直したか */
export const MailTemplate = z.object({ subject: z.string(), body: z.string(), source: z.enum(["default", "tenant"]) });
export const MailTemplates = z.object({ quote: MailTemplate, invoice: MailTemplate, receipt: MailTemplate, rfq: MailTemplate, order: MailTemplate, revise: MailTemplate, decline: MailTemplate, payment_notice: MailTemplate, delivery_note: MailTemplate, acceptance: MailTemplate });
export type MailTemplates = z.infer<typeof MailTemplates>;
const TemplateText = z.object({
  subject: z.string().trim().min(1, MAIL_MSG.subjectRequired).max(MAIL_LIMITS.templateSubject),
  body: z.string().trim().min(1, MAIL_MSG.bodyRequired).max(MAIL_LIMITS.templateBody),
});
/** PUT /company/mail-templates：種類ごとに { subject, body }（省けば変えない・null で既定に戻す） */
export const MailTemplatesUpdate = z
  .object({ quote: TemplateText.nullable(), invoice: TemplateText.nullable(), receipt: TemplateText.nullable(), rfq: TemplateText.nullable(), order: TemplateText.nullable(), revise: TemplateText.nullable(), decline: TemplateText.nullable(), payment_notice: TemplateText.nullable(), delivery_note: TemplateText.nullable(), acceptance: TemplateText.nullable() })
  .partial()
  .superRefine((v, ctx) => {
    for (const [k, t] of Object.entries(v)) {
      if (t && hasUrl(t.subject)) ctx.addIssue({ code: "custom", path: [k, "subject"], message: MAIL_MSG.urlNotAllowed });
      if (t && hasUrl(t.body)) ctx.addIssue({ code: "custom", path: [k, "body"], message: MAIL_MSG.urlNotAllowed });
    }
  });
export type MailTemplatesUpdate = z.infer<typeof MailTemplatesUpdate>;

/** 取得のお知らせ（GET /mail-notices。送った人の分だけ・まだ知らせていないもの） */
export const MailNotice = z.object({ deliveryId: z.string().uuid(), to: z.string(), docs: z.string(), at: z.string() });
export type MailNotice = z.infer<typeof MailNotice>;
export const MailNotices = z.object({ items: z.array(MailNotice) });
export type MailNotices = z.infer<typeof MailNotices>;

/** ホームの「メールの確認」（★4） */
export const HomeMailWatch = z.object({ bounced: z.number().int(), unopened: z.number().int() });
export type HomeMailWatch = z.infer<typeof HomeMailWatch>;

/** 閲覧ページ（GET /api/v1/d/:token）：送った会社名・件名・送信日・公開期限・ファイル。受け取る人の名前・宛先は返さない */
export const DeliveryPublicPage = z.object({
  company: z.string(),
  product: z.enum(["genba", "eigyo"]),
  subject: z.string(),
  sentAt: z.string(),
  expiresAt: z.string(),
  files: z.array(z.object({ no: z.number().int(), label: z.string(), docNo: z.string().nullable(), fileName: z.string(), contentType: z.string(), bytes: z.number().int(), pages: z.number().int().nullable() })),
  /** 同封した{deliveryNote}・{acceptance}の受領・検収のページ（#266 1b。path＝/a/<鍵>・done＝もう済んだ） */
  acks: z.array(z.object({ label: z.string(), docNo: z.string().nullable(), kind: z.enum(["receive", "accept"]), path: z.string(), done: z.boolean() })).default([]),
});
export type DeliveryPublicPage = z.infer<typeof DeliveryPublicPage>;
/** 閲覧ページの鍵：32バイトの乱数を base64url（43字）。形が違えば API は 404（DB まで行かない） */
export const DELIVERY_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;
/** 閲覧ページの道（製品のドメインの /d/<鍵>） */
export const deliveryPagePath = (token: string): string => `/d/${token}`;
